import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createMedicineOrderServiceClient, type CreateMedicineOrderInput, type MedicineOrder } from "./client";

export type MedicineOrdersStatus = "idle" | "loading" | "ready" | "saving" | "error";

export function useMedicineOrders() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.medicineOrderService?.apiBaseUrl;
  const [orders, setOrders] = useState<MedicineOrder[]>([]);
  const [status, setStatus] = useState<MedicineOrdersStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createMedicineOrderServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadOrders = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Medicine Orders are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);

      try {
        const records = await client.listOrders(signal);
        setOrders(records);
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your medicine orders.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadOrders(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadOrders]);

  const createOrder = useCallback(
    async (payload: CreateMedicineOrderInput) => {
      if (!client) {
        setError("Medicine Orders are not available yet.");
        setStatus("error");
        return null;
      }

      setStatus("saving");
      setError(null);

      try {
        const record = await client.createOrder(payload);
        setOrders((current) => [record, ...current]);
        setStatus("ready");
        return record;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not place your medicine order.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  // A direct, single-record fetch -- used by the order detail page so a refreshed or
  // directly-opened /app/orders/:orderId still resolves correctly without depending on the full
  // list above having already loaded into this hook instance.
  const getOrder = useCallback(
    async (orderId: string, signal?: AbortSignal) => {
      if (!client) {
        return null;
      }

      try {
        return await client.getOrder(orderId, signal);
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return null;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load that medicine order.");
        return null;
      }
    },
    [client],
  );

  return {
    createOrder,
    error,
    getOrder,
    loadOrders,
    orders,
    status,
  };
}
